import { forwardRef, useCallback } from 'react'
import { Button, ButtonProps, Link, LinkProps } from '@mui/material'
import { NavigateOptions, useNavigate } from 'react-router-dom'

type LoadedLinkProps = {
  to: string
  layout?: 'link' | 'button'
  openNewTab?: boolean
  isExternalUrl?: boolean
  navigationOptions?: NavigateOptions
  handleClick?: (event) => void
} & (LinkProps | ButtonProps)

const LoadedLink = forwardRef<HTMLAnchorElement, LoadedLinkProps>(
  (
    {
      children,
      to,
      layout = 'link',
      openNewTab = false,
      isExternalUrl = false,
      navigationOptions,
      handleClick,
      ...props
    },
    ref
  ) => {
    const navigate = useNavigate()

    const onClick = useCallback(
      (event) => {
        handleClick?.(event)

        let shouldUseNewTab = openNewTab

        // If the user is ctrl/command clicking, middle clicking, or the
        // prop forcing a new tab, let the browser handle it naturally
        if (event.ctrlKey || event.metaKey || event.button === 1) {
          shouldUseNewTab = true
        }

        if (!shouldUseNewTab) {
          event.preventDefault()
          window.dispatchEvent(new CustomEvent('@@loaded/reactNavigation'))
          navigate(to, navigationOptions)
        }
        // For new tab opens, let the browser's default behavior handle it
      },
      [handleClick, openNewTab, navigate, to, navigationOptions]
    )

    const href = isExternalUrl ? to : `${window.location.pathname}#${to}`

    return layout === 'link' ? (
      <Link
        // We render as an anchor tag (not component="button") to enable browser
        // link features: right-click "Open in new tab", middle-click, Ctrl/Cmd+click,
        // and proper accessibility.
        href={href}
        target={openNewTab ? '_blank' : undefined}
        rel={openNewTab ? 'noopener noreferrer' : undefined}
        underline="none"
        onClick={onClick}
        {...(props as LinkProps)}
        ref={ref}
        sx={{
          // Normalize anchor tag to behave like a button to maintain consistency
          // with the previous component="button" implementation and prevent styling issues.
          display: 'inline-block',
          verticalAlign: 'middle', // Works in non-flex contexts
          alignSelf: 'center', // Works in flex containers
          textAlign: 'center',
          '&:hover': {
            color: 'inherit',
          },
          ...(props as LinkProps).sx,
        }}
      >
        {children}
      </Link>
    ) : (
      <Button
        component="a"
        href={href}
        target={openNewTab ? '_blank' : undefined}
        rel={openNewTab ? 'noopener noreferrer' : undefined}
        onClick={onClick}
        variant="contained"
        color="info"
        {...(props as ButtonProps)}
      >
        {children}
      </Button>
    )
  }
)

LoadedLink.displayName = 'LoadedLink'

export default LoadedLink
